/**
 * Agent Playbook — boundary contract schemas for the landlord's per-agent playbook
 * (customizations of the runtime's quality catalog + landlord-authored rules).
 *
 * Owned here: `apps/api/src/routes/manager/agent-playbook.routes.ts` registers these
 * schemas and the landlord Settings > Agents page derives its types from the same objects.
 * See packages/api-types/README.md.
 *
 * Covers:
 *   GET  /landlord/api/agent-playbooks
 *   POST /landlord/api/agent-playbooks/:agentKind
 *   GET|PATCH /landlord/api/agent-policy/default/:agentKind  (params schema only)
 */
import { z } from 'zod';
/** Max landlord rules per playbook (BR-PB-VALIDATE). */
export const PLAYBOOK_MAX_RULES = 25;
/** Max trimmed length of a rule / rewritten default text (BR-PB-VALIDATE). */
export const PLAYBOOK_TEXT_MAX = 500;
/** Agent kinds that have a playbook (and a per-agent landlord default policy). */
export const playbookAgentKindSchema = z.enum(['repair', 'leasing']);
/** One item of the runtime's quality catalog, as shown to the landlord. */
export const catalogItemSchema = z.object({
    id: z.string(),
    tier: z.enum(['locked', 'default']),
    title: z.string(),
});
/**
 * Where a playbook document applies (BR-PB-SCOPE): the landlord's all-properties document, or one
 * property's. Absent on a save means `{ kind: 'landlord' }`.
 */
export const ruleScopeSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('landlord') }),
    z.object({ kind: z.literal('property'), propertyId: z.uuid() }),
]);
/** A landlord's customization of a catalog default: turned off, reworded, or (property scope only) turned back on. */
export const playbookDefaultCustomizationSchema = z
    .object({
    itemId: z.string().min(1).max(100),
    state: z.enum(['off', 'rewritten', 'on']),
    text: z.string().trim().min(1).max(PLAYBOOK_TEXT_MAX).optional(),
})
    .refine((d) => (d.state === 'rewritten') === (d.text !== undefined), {
    message: 'text is required when state is "rewritten" and forbidden otherwise',
    path: ['text'],
});
/**
 * A landlord-authored rule as sent on save. Strict: exactly `{id?, text}` — `source` and
 * `conversationId` are the runtime's to set and a body carrying them (or any other key) is refused
 * (BR-PB-ENVELOPE).
 */
export const playbookRuleSchema = z.strictObject({
    id: z.uuid().optional(),
    text: z.string().trim().min(1).max(PLAYBOOK_TEXT_MAX),
});
export const saveAgentPlaybookBodySchema = z.object({
    ruleScope: ruleScopeSchema.optional(),
    defaults: z.array(playbookDefaultCustomizationSchema).max(100),
    rules: z.array(playbookRuleSchema).max(PLAYBOOK_MAX_RULES),
});
export const playbookDefaultViewSchema = playbookDefaultCustomizationSchema.safeExtend({
    baseTitle: z.string(),
    reworded: z.boolean(),
    retired: z.boolean(),
});
/** A rule as read back: its envelope says where it came from (BR-PB-ENVELOPE). */
export const playbookRuleViewSchema = z.object({
    id: z.uuid(),
    text: z.string(),
    source: z.enum(['editor', 'conversation']),
    conversationId: z.string().optional(),
});
/** One property's playbook document (BR-PB-SCOPE). */
export const playbookPropertyScopeViewSchema = z.object({
    propertyId: z.uuid(),
    propertyName: z.string(),
    versionId: z.string().min(1).max(200),
    defaults: z.array(playbookDefaultViewSchema),
    rules: z.array(playbookRuleViewSchema),
    counts: z.object({
        rules: z.number().int().nonnegative(),
        defaultsOff: z.number().int().nonnegative(),
        defaultsReworded: z.number().int().nonnegative(),
    }),
});
export const agentPlaybookKindViewSchema = z.object({
    agentKind: playbookAgentKindSchema,
    catalogVersion: z.string(),
    items: z.array(catalogItemSchema),
    versionId: z.string().min(1).max(200).nullable(),
    savedAt: z.iso.datetime().nullable(),
    savedByUserId: z.uuid().nullable(),
    customized: z.boolean(),
    defaults: z.array(playbookDefaultViewSchema),
    rules: z.array(playbookRuleViewSchema),
    propertyScopes: z.array(playbookPropertyScopeViewSchema),
});
export const agentPlaybooksResponseSchema = z.object({
    playbooks: z.array(agentPlaybookKindViewSchema),
});
export const playbookValidationErrorResponseSchema = z.object({
    errors: z.array(z.object({ path: z.string(), message: z.string() })),
});
export const agentPlaybookKindParamsSchema = z.object({
    agentKind: playbookAgentKindSchema,
});
//# sourceMappingURL=playbook.js.map